/**
 * The toast queue, with no React in it.
 *
 * Everything that decides WHICH toasts exist and which three are on screen
 * lives here so it can be tested in the node environment vitest runs (see
 * vitest.config.ts — `environment: 'node'`, `include: src/**\/*.spec.ts`).
 * The React side (ToastProvider / ToastItem) only subscribes and paints.
 *
 * Before this there were three parallel channels: `StudioShell.setToast`
 * (string only, no error variant — tracker C-43), the flow-ops pair
 * `ops.toastMessage` + `ops.toastVariant`, and `useProductData.toast`, each
 * rendering its own `<Toast>` at the top of the page. One queue replaces them.
 */

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Milliseconds on screen. 0 means "until dismissed". Defaults per variant. */
  duration?: number;
  action?: ToastAction;
}

export interface ToastRecord {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
  action?: ToastAction;
}

/** Three is what fits above the fold without becoming a wall; the rest queue. */
export const MAX_VISIBLE = 3;

/**
 * An error stays until the reader dismisses it: it is the one variant that
 * carries something the reader still has to act on, and a 4-second error is an
 * error nobody read.
 */
export const DEFAULT_DURATION: Record<ToastVariant, number> = {
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 0,
};

export interface ToastStore {
  subscribe: (listener: () => void) => () => void;
  /** The at-most-MAX_VISIBLE oldest entries, stable by reference between changes. */
  getVisible: () => readonly ToastRecord[];
  push: (message: string, variant: ToastVariant, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export function createToastStore(): ToastStore {
  let queue: ToastRecord[] = [];
  let visible: readonly ToastRecord[] = [];
  let seq = 0;
  const listeners = new Set<() => void>();

  const recompute = () => {
    const next = queue.slice(0, MAX_VISIBLE);
    // useSyncExternalStore re-renders forever if the snapshot is a new array
    // every call, so only swap the reference when the contents actually moved.
    const same = next.length === visible.length && next.every((t, i) => t === visible[i]);
    if (!same) visible = next;
  };

  const emit = () => {
    recompute();
    listeners.forEach((l) => l());
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    getVisible: () => visible,

    push: (message, variant, options) => {
      const id = `t${++seq}`;
      queue = [...queue, {
        id,
        message,
        variant,
        duration: options?.duration ?? DEFAULT_DURATION[variant],
        action: options?.action,
      }];
      emit();
      return id;
    },

    dismiss: (id) => {
      const next = queue.filter((t) => t.id !== id);
      if (next.length === queue.length) return;
      queue = next;
      emit();
    },

    clear: () => {
      if (queue.length === 0) return;
      queue = [];
      emit();
    },
  };
}
